/* The floor: the worst screen this console can show anybody.
 *
 * Until now that screen was `@tanstack/react-router`'s built-in `ErrorComponent` — bare
 * `Something went wrong!` over an unstyled red `<pre>`, reached because `createRouter` set
 * a pending and a not-found component but no error one. It was not ours, it named no
 * recovery, and it left no trace anybody could look up afterwards.
 *
 * The direction is a focused recovery rather than incident response. A route failure is
 * reported automatically with the route and component stack, while the person sees only
 * the recovery that can help: reload or return to their projects. A customer cannot act on
 * an incident identifier or a browser stack, and neither belongs in the product surface.
 *
 * Two rules hold this file together, because they are why the screen exists:
 *
 *   - It must never throw. No data fetching, no router context, no workspace state — a
 *     plain `<a href="/">` rather than a `<Link>`, because if the router is what failed a
 *     `Link` takes the fallback down with it. Every browser API it touches is guarded.
 *   - It must not lie. "Reload" is `location.reload()`, not the router's `reset()`: reset
 *     re-renders the same boundary against the same bad data and throws straight back,
 *     which reads as a dead button. A hard reload re-fetches, which is the honest recovery.
 */

import { useEffect, useRef } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Icon } from "@sx/ui";
import { Button } from "@sx/ui";

import { reportRenderIncident } from "../lib/incident";

export function RouteError({ error }: ErrorComponentProps) {
  const heading = useRef<HTMLHeadingElement>(null);

  // Backstop, for the paths that reach an error component without a boundary catch. Both
  // sides are idempotent, so whichever runs first owns the report and the other is a no-op.
  useEffect(() => {
    reportRenderIncident(error, { surface: "route" });
  }, [error]);

  // Move the caret to the headline: the route swapped under a keyboard or screen-reader
  // user without their input, and focus would otherwise sit on a control that is gone.
  useEffect(() => heading.current?.focus(), []);

  return (
    <div className="grid h-full min-h-[32rem] place-items-center bg-background px-6 py-16">
      {/* A plain div, not <main>: this renders wherever the throw happened, and a leaf
          route's failure leaves the shell's own <main> mounted around it. A second, nested
          landmark is worse for a screen reader than none, and the focused <h1> below is
          what actually orients someone here. The `pageSources` landmark guard does not
          scan `shell/`, so this one is on the author. */}
      <div className="w-full max-w-md duration-500 animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none">
        <h1
          ref={heading}
          tabIndex={-1}
          className="mt-3 font-sans text-page-title font-semibold text-foreground outline-none"
        >
          We couldn’t open this workspace.
        </h1>
        <p className="mt-3 text-body text-muted-foreground">
          Reload to try again. Anything you already saved remains in your project.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={() => window.location.reload()}>
            <Icon name="refresh" className="h-4 w-4" />
            Reload
          </Button>
          <Button asChild variant="outline">
            <a href="/">Go to projects</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
